import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
}

/**
 * ErrorBoundary component to catch JavaScript errors anywhere in the child component tree.
 * Displays a fallback UI instead of crashing the entire application.
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        }
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log the error to console in development
        console.error('ErrorBoundary caught an error:', error)
        console.error('Error info:', errorInfo)

        this.setState({ errorInfo })

        // In production, you could log to an error reporting service here
        // Example: logErrorToService(error, errorInfo)
    }

    handleRetry = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        })
    }

    handleGoHome = (): void => {
        window.location.href = '/dashboard'
    }

    handleReload = (): void => {
        window.location.reload()
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback UI provided
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI - maintains existing design system
            return (
                <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--background)' }}>
                    <div
                        className="max-w-md w-full rounded-2xl p-8 text-center"
                        style={{
                            backgroundColor: 'var(--card-background)',
                            border: '1px solid var(--border-color)',
                            boxShadow: 'var(--shadow-lg)'
                        }}
                    >
                        {/* Error Icon */}
                        <div
                            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
                            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                        >
                            <AlertTriangle size={32} style={{ color: '#ef4444' }} />
                        </div>

                        {/* Error Title */}
                        <h1
                            className="text-2xl font-bold mb-2"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            Something went wrong
                        </h1>

                        {/* Error Description */}
                        <p
                            className="mb-6"
                            style={{ color: 'var(--text-secondary)' }}
                        >
                            We encountered an unexpected error. Don't worry, your data is safe.
                        </p>

                        {/* Error Details (Development Only) */}
                        {import.meta.env.DEV && this.state.error && (
                            <details
                                className="mb-6 text-left rounded-lg p-4"
                                style={{
                                    backgroundColor: 'var(--background)',
                                    border: '1px solid var(--border-color)'
                                }}
                            >
                                <summary
                                    className="cursor-pointer font-medium mb-2"
                                    style={{ color: 'var(--text-primary)' }}
                                >
                                    Error Details
                                </summary>
                                <pre
                                    className="text-xs overflow-auto p-2 rounded"
                                    style={{
                                        color: '#ef4444',
                                        backgroundColor: 'rgba(239, 68, 68, 0.05)'
                                    }}
                                >
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack && (
                                        <>
                                            {'\n\nComponent Stack:'}
                                            {this.state.errorInfo.componentStack}
                                        </>
                                    )}
                                </pre>
                            </details>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={this.handleRetry}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200"
                                style={{
                                    backgroundColor: 'var(--primary)',
                                    color: 'white'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <RefreshCw size={18} />
                                Try Again
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200"
                                style={{
                                    backgroundColor: 'var(--background)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-color)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--background)'}
                            >
                                <Home size={18} />
                                Go to Dashboard
                            </button>
                        </div>

                        {/* Help Text */}
                        <p
                            className="mt-6 text-sm"
                            style={{ color: 'var(--text-muted)' }}
                        >
                            If this problem persists, please contact support or{' '}
                            <button
                                onClick={this.handleReload}
                                className="underline hover:no-underline"
                                style={{ color: 'var(--primary)' }}
                            >
                                reload the page
                            </button>
                        </p>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
